import React, { useMemo } from "react";
import { ContentDropdown, TooltipMessage, TooltipTrigger } from "@coverme/elements";
import { FacesheetPersonal } from "../FacesheetPersonal/FacesheetPersonal";
import { FacesheetOtherFinancial } from "../FacesheetOtherFinancial/FacesheetOtherFinancial";
import "./FacesheetAccordion.scss";
import { getFormattedName } from "../../services/stringService";
import { createHouseholdMembersFromInterview } from "../../services/magiServices";
import { FacesheetDocuments } from "../FacesheetDocuments/FacesheetDocuments";
import { FacesheetNotes } from "../Notes/FacesheetNotes";
import { FacesheetHistory } from "../FacesheetHistory/FacesheetHistory";
import { FacesheetThirdPartyLiability } from "../FacesheetThirdPartyLiability/FacesheetThirdPartyLiability";
import { FacesheetHousehold2 } from "../FacesheetHousehold/FacesheetHouseholds";
import { FacesheetVerification } from "../FacesheetPersonal/FacesheetVerification";
import { FacesheetLTCReferral } from "../FacesheetLTCReferral/FacesheetLTCReferral";
import { FacesheetLTCIPR } from "../FacesheetLTCIPR/FacesheetLTCIPR";
import { FacesheetScore } from "../FacesheetScore/FacesheetScore";
import { DigitalSignatureDocuments } from "../FacesheetDigitalSignatureDocuments/DigitalSignatureDocuments";
import { Tooltip } from "../Tooltips/Tooltip";
import { Icon } from "@coverme/icons";
import { FacesheetCaseClues } from "../FacesheetScore/FacesheetCaseClues";
import { FacesheetFAPDocumentGroup } from "../FacesheetFAP/FacesheetFAPDocumentGroup";
import { FacesheetCoverage } from "../FacesheetCoverage/FacesheetCoverage";

export const FacesheetAccordion = ({
    interview,
    documents,
    getDocuments,
    documentTypes,
    deleteDocument,
    households,
    hasDeleteDocumentPermissions,
    notes = [],
    isNotesLoading,
    onNoteAdded,
    programs,
    scores,
    criticalFactors,
    isIncomplete,
    generatedPdfsForEncounter,
    pdfList,
    customer,
    encounterId,
    shouldShowUmmcNavigation,
    signedPdfList,
    printedPdfList,
    caseClues,
    faDocumentation,
    faxHistory,
    isFaDocumentationLoading,
    shouldShowFAPSection,
}) => {
    const patientId = interview?.entry?.patient?.personId;
    const patientName = getFormattedName(
        interview.entry.patient.firstName,
        interview.entry.patient.middleInitial,
        interview.entry.patient.lastName,
        interview.entry.patient.suffix
    );
    const patientDocuments = documents.data[patientId] || [];
    const householdMembers = createHouseholdMembersFromInterview(interview);

    function showReferralSection() {
        return (
            interview.longTermCare?.referralDetails?.referralContactDetails?.firstName ||
            interview.longTermCare?.referralDetails?.referralContactDetails?.lastName ||
            interview.longTermCare?.referralDetails?.referralSource ||
            interview.longTermCare?.referralDetails?.referralContactDetails?.phoneNumber ||
            interview.longTermCare?.referralDetails?.referralFacilityName ||
            interview.longTermCare?.additionalContactDetails?.firstName ||
            interview.longTermCare?.additionalContactDetails?.lastName ||
            interview.longTermCare?.additionalDetails?.relationToPatient ||
            interview.longTermCare?.additionalDetails?.organizationName ||
            interview.longTermCare?.additionalDetails?.phoneNumber
        );
    }

    const formattedSignedPdfList = useMemo(() => {
        return signedPdfList?.map(x => {
            const removeYearsFromFederal = /20[0-9]{2} Federal/;
            return {
                ...x,
                documentsInBatch: x.documentsInBatch.map(s => {
                    return s.replace(removeYearsFromFederal, "Federal");
                }),
                printDate: printedPdfList?.filter(y => y.documentID === x.documentID)[0]?.printDate,
            };
        });
    }, [pdfList, printedPdfList, signedPdfList]);

    const financialDocumentsDropDownItems = useMemo(() => {
        if (pdfList == null || pdfList.length === 0) {
            return [];
        }

        return pdfList.map(x => ({
            value: x.key,
            label: x.name,
        }));
    }, [pdfList]);

    const coverageTitle = useMemo(() => {
        if (interview.coverage?.coverageSummary?.personCoverages?.filter(x => x.isActive)?.length === 0) {
            return "No Coverage Found";
        }

        const primaryCoverage = interview.coverage?.coverageSummary?.personCoverages?.filter(
            x => x.isPrimaryCoverage && x.isActive
        )[0];

        if (primaryCoverage?.companyName != null) {
            return primaryCoverage.companyName;
        }

        return "No Coverage Found";
    }, [interview.coverage]);

    return (
        <div className="facesheet-accordion">
            {interview.encounterId ? (
                <div>
                    <h4 className="facesheet-accordion__title">Patient Overview</h4>
                    <div className="facesheet-accordion__content">
                        <ContentDropdown title="Personal" estimatedHeight={1000000}>
                            <div>
                                <FacesheetPersonal
                                    personal={interview.entry.patient}
                                    tax={interview.personInfoScreening}
                                />
                            </div>
                        </ContentDropdown>
                        <ContentDropdown title="Coverage" estimatedHeight={1000000} subtitle={coverageTitle}>
                            <FacesheetCoverage
                                customerId={customer?.data?.customer?.GlobalId}
                                encounterId={encounterId}
                                coverage={interview.coverage}
                                patientFullName={patientName}
                            />
                        </ContentDropdown>
                        <ContentDropdown title="Verification" estimatedHeight={1000000}>
                            <div>
                                <FacesheetVerification verifications={interview.verifications} />
                            </div>
                        </ContentDropdown>
                        {interview.coverage.thirdPartyLiability?.typeOfAccident!= null && interview.coverage.thirdPartyLiability?.additionalNotes != null
                        && interview.coverage.thirdPartyLiability?.typeOfAccident != "" && (
                            <ContentDropdown title="Third Party Liability" estimatedHeight={1000000}>
                                <div>
                                    <FacesheetThirdPartyLiability
                                        thirdPartyLiability={interview.coverage.thirdPartyLiability}
                                    />
                                </div>
                            </ContentDropdown>
                        )}

                        <FacesheetHousehold2
                            interviewHouseholdMembers={householdMembers}
                            interview={interview}
                            households={households}
                        />
                        {scores?.fitScore > 0 &&
                            scores?.riskScore > 0 &&
                            scores?.reliabilityScore > 0 &&
                            programs[0]?.programDetails?.Title != "LTC" && (
                                <ContentDropdown title="Confidence Scores" estimatedHeight={10000}>
                                    <div>
                                        <FacesheetScore
                                            scores={scores}
                                            criticalFactors={criticalFactors}
                                            isIncomplete={isIncomplete}
                                        />
                                    </div>
                                </ContentDropdown>
                            )}

                        {programs?.length > 0 && programs[0]?.score === null && caseClues?.data?.length > 0 && (
                            <ContentDropdown title="Case Clues" estimatedHeight={10000}>
                                <div>
                                    <FacesheetCaseClues caseClues={caseClues.data} />
                                </div>
                            </ContentDropdown>
                        )}

                        {showReferralSection() && (
                            <ContentDropdown title="Referral" estimatedHeight={10000}>
                                <div>
                                    <FacesheetLTCReferral
                                        referralDetails={interview.longTermCare.referralDetails}
                                        additionalDetails={interview.longTermCare.additionalContactDetails}
                                    />
                                </div>
                            </ContentDropdown>
                        )}
                        {programs?.length > 0 && programs[0]?.score?.reasons?.length > 0 && (
                            <ContentDropdown title="Identified Program Risks" estimatedHeight={10000}>
                                <div>
                                    <FacesheetLTCIPR risks={programs} caseClues={caseClues} />
                                </div>
                            </ContentDropdown>
                        )}
                        {(interview.liabilities.debts.length > 0 ||
                            interview.liabilities.expenses.length > 0 ||
                            interview.assets.assets.length > 0) && (
                            <ContentDropdown title="Other Financial" estimatedHeight={10000}>
                                <div>
                                    <FacesheetOtherFinancial
                                        expenses={interview.liabilities.expenses}
                                        assets={interview.assets?.assets}
                                        debts={interview.liabilities.debts}
                                    />
                                </div>
                            </ContentDropdown>
                        )}

                        <ContentDropdown title="Documents" estimatedHeight={1000000}>
                            <FacesheetDocuments
                                personId={patientId}
                                getDocuments={getDocuments}
                                isLoading={documents.isLoading === true || documents.isInitialized === false}
                                patientName={patientName}
                                documentTypes={documentTypes}
                                documents={patientDocuments}
                                deleteDocument={deleteDocument}
                                hasDeletePermission={hasDeleteDocumentPermissions}
                                customerId={customer?.data?.customer?.GlobalId}
                            />
                        </ContentDropdown>
                        {shouldShowFAPSection && (
                            <ContentDropdown title="FAP Documentation" estimatedHeight={1000000}>
                                <FacesheetFAPDocumentGroup
                                    isFaDocumentationLoading={isFaDocumentationLoading}
                                    faDocumentation={faDocumentation}
                                    financialDocumentsDropDownItems={financialDocumentsDropDownItems}
                                    printedPdfs={printedPdfList}
                                />
                            </ContentDropdown>
                        )}

                        {formattedSignedPdfList?.length > 0 && (
                            <ContentDropdown
                                title={
                                    <>
                                        <>Digital Signature Requests</>{" "}
                                        <div
                                            style={{
                                                marginLeft: "10px",
                                                marginTop: "5px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                zIndex: "100000",
                                            }}
                                        >
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <Icon icon="tooltip" />
                                                </TooltipTrigger>
                                                <TooltipMessage>
                                                    <p>
                                                        The document access code is the last 4 digits of SSN, if SSN is
                                                        not provided then the document cannot be sent via text or email.
                                                    </p>
                                                </TooltipMessage>
                                            </Tooltip>
                                        </div>
                                    </>
                                }
                                estimatedHeight={1000000}
                            >
                                <DigitalSignatureDocuments formattedSignedPdfList={formattedSignedPdfList} />
                            </ContentDropdown>
                        )}

                        <ContentDropdown title={`Notes`} estimatedHeight={1000000} subtitle={`Count: ${notes.length}`}>
                            <FacesheetNotes notes={notes} isNotesLoading={isNotesLoading} onNoteAdded={onNoteAdded} />
                        </ContentDropdown>
                        <ContentDropdown title={`History`} estimatedHeight={1000000}>
                            <FacesheetHistory
                                encounterId={encounterId}
                                encounterHistory={interview.history}
                                patientId={interview?.entry?.patient?.personId}
                                patientName={patientName}
                                generatedPdfsForEncounter={generatedPdfsForEncounter}
                                pdfList={pdfList}
                                shouldShowUmmcNavigation={shouldShowUmmcNavigation}
                                faxHistory={faxHistory}
                            />
                        </ContentDropdown>
                    </div>
                </div>
            ) : (
                <div>
                    <h2>Loading</h2>
                </div>
            )}
        </div>
    );
};
